import { isPstnNumber, normalizeDialString } from "@/lib/phone-number";
import type { AccidentRecord, LeadAccident, LeadSibling } from "@/lib/types";

/**
 * "Seen before" (ACL-409): whether a lead or a number has a history with us, said where a rep first
 * meets it: the record's tag, the panel in the phone's rail, the line on the dialer's offer and the
 * new-contact form. The records are the ones the record's cards already list (the same phone,
 * ACL-336; the same e-mail and the people in its accident, ACL-337), each counted once, and the one
 * fact about them that matters most. Shown, never decided: nothing here links, merges or refuses
 * anything. Pure, for `test/seen-before.test.ts`.
 */

type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "outline";

/** Its name everywhere. "Call history" and "My calls" already have "history" in theirs. */
export const SEEN_BEFORE = "Seen before";

/** The record's two cards that hold the same lists, which the tag scrolls to where there is no rail. */
export const SAME_PHONE_CARD_ID = "same-phone";
export const ACCIDENT_CARD_ID = "accident";

/** The one fact that matters most about a lead's other records, and how loudly it is said. */
export interface SeenBeforeFact {
  kind: "signed" | "dnc" | "not_qualified" | "same_accident";
  text: string;
  tone: Tone;
}

/** How many other records a lead has, and the fact that matters most about them. */
export interface SeenBefore {
  count: number;
  fact: SeenBeforeFact | null;
}

/** What one listed record tells the fact: its stage and do-not-call, which every reader sees, as the
 *  Same phone card shows them; the firm's reason, which only a reader who may open it ever receives;
 *  and whether somebody said it is the same accident. */
interface Seen {
  stage: string | null;
  dnc: boolean;
  reason: string | null;
  sameAccident: boolean;
}

const SIGNED = new Set(["signed", "signed_pending_intake"]);

/**
 * A lead's other records, from the card's records with its phone (`siblings`) and its accident's
 * (`records`: the phone's again, the e-mail's and the people linked in), each once: how many, and
 * the fact that matters most, in this order: signed, do not call, not qualified (with the firm's
 * reason, where the reader may open that record), the same accident. Null when there are none.
 *
 * A record the reader may not open still gives its stage and do-not-call, as the Same phone card
 * and the ringing line already show them. Its name, outcome and reason are not sent to this reader,
 * so they cannot reach the tag.
 */
export function seenBefore(
  siblings: readonly LeadSibling[] | undefined,
  accident: LeadAccident | undefined,
): SeenBefore | null {
  const seen = new Map<string, Seen>();
  for (const s of siblings ?? []) {
    seen.set(s.id, { stage: s.stage, dnc: s.dnc, reason: s.decline_reason?.trim() || null, sameAccident: false });
  }
  for (const r of accident?.records ?? []) {
    const known = seen.get(r.id);
    seen.set(r.id, {
      stage: known?.stage ?? r.stage,
      dnc: (known?.dnc ?? false) || r.dnc,
      reason: known?.reason ?? null,
      sameAccident: r.why === "linked" || r.relation === "same_accident",
    });
  }
  if (seen.size === 0) return null;
  return { count: seen.size, fact: factOf([...seen.values()]) };
}

function factOf(records: Seen[]): SeenBeforeFact | null {
  const signed = records.filter((r) => r.stage !== null && SIGNED.has(r.stage)).length;
  if (signed > 0) return { kind: "signed", text: signed === 1 ? "Signed" : `${signed} signed`, tone: "success" };
  if (records.some((r) => r.dnc || r.stage === "dnc")) return { kind: "dnc", text: "Do not call", tone: "danger" };
  const turnedDown = records.filter((r) => r.stage === "not_qualified");
  if (turnedDown.length > 0) {
    const reason = turnedDown.find((r) => r.reason)?.reason;
    return { kind: "not_qualified", text: reason ? `Not qualified: ${reason}` : "Not qualified", tone: "warning" };
  }
  if (records.some((r) => r.sameAccident)) return { kind: "same_accident", text: "Same accident", tone: "accent" };
  return null;
}

/** "1 other record", "3 other records". */
export function recordsText(count: number): string {
  return count === 1 ? "1 other record" : `${count} other records`;
}

/** A record of the accident's lists as the Same phone list draws a record, so the panel draws the
 *  e-mail's and the accident's records the way it draws the phone's. They carry no firm's reason or
 *  note: the accident's route does not send them. */
export function asSibling(record: AccidentRecord): LeadSibling {
  return {
    id: record.id,
    stage: record.stage,
    dnc: record.dnc,
    firm: record.firm,
    created_at: record.created_at,
    deleted: record.deleted,
    may_open: record.may_open,
    first_name: record.first_name,
    last_name: record.last_name,
    disposition: record.disposition,
    disposition_label: record.disposition_label,
  };
}

/**
 * Whether the rail's tooltip says so by itself on a visit to a record, once the record's lists are in:
 * only for a record with something to show, and never while the phone is ringing, open, or suggesting
 * a voicemail, where it would sit beside the call the rep is on. Decided once, at that moment, as the
 * phone opens itself only when a call arrives: a tooltip that waited and showed when the phone closed
 * would show under the rep's hand. Null while the lists are still coming.
 */
export function showsHint({ settled, count, phoneBusy }: { settled: boolean; count: number; phoneBusy: boolean }): boolean | null {
  if (!settled) return null;
  return count > 0 && !phoneBusy;
}

/** How long the tooltip that showed by itself stays, as long as the voicemail suggestion's choices
 *  that opened by themselves (`SUGGESTION_SHOWN_MS`): it sits beside what the rep is reading. */
export const HINT_SHOWN_MS = 20_000;

/**
 * The phone the new-contact form asks about: a whole US number, however it is typed, or another
 * country's written with its code (E.164); null while it is still being typed. A number written with
 * +1 is asked about only once it is whole, as a bare one is: `+13105550` is not a number yet. Another
 * country's numbers are of many lengths, so there the pause in the typing decides.
 */
export function phoneToAsk(typed: string): string | null {
  const dial = normalizeDialString(typed);
  if (isPstnNumber(dial)) return dial;
  return /^\+[2-9]\d{7,14}$/.test(dial) ? dial : null;
}

/** The e-mail the form asks about, once it looks like one (the form's own test). */
export function emailToAsk(typed: string): string | null {
  const value = typed.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? value : null;
}
